import { FilterAltOff, Search } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useToastStore } from "../../../stores";
import { colorPrimary } from "../../../theme";
import { fetchGrupoConcessionaria } from "../repository/AdminRepository";
import {
  GrupoConcessionaria,
  ParametrosGrupoConcessionaria,
  RelatorioFilterValues,
} from "../types/types";

interface AutocompleteOption {
  id: string;
  label: string;
}

const TODOS_GRUPOS: AutocompleteOption = { id: "todos", label: "Todos os Grupos" };
const TODOS_CONCESSIONARIAS: AutocompleteOption = { id: "todos", label: "Todas as Concessionárias" };

interface RelatorioFilterBarProps {
  values: RelatorioFilterValues;
  onChange: (values: RelatorioFilterValues) => void;
  onSearch: () => void;
  onClear?: () => void;
  loading: boolean;
  title?: string;
}

function toOptions(items: ParametrosGrupoConcessionaria[]): AutocompleteOption[] {
  return items.map((i) => ({ id: String(i.id), label: i.label }));
}

const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "white",
    borderRadius: 2,
    fontSize: "0.85rem",
    "& fieldset": { border: "1px solid #e2e8f0" },
    "&:hover fieldset": { borderColor: colorPrimary + "80" },
    "&.Mui-focused fieldset": { borderColor: colorPrimary },
  },
  "& .MuiAutocomplete-popupIndicator": { color: "#94a3b8" },
  "& .MuiAutocomplete-clearIndicator": { color: "#94a3b8" },
} as const;

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "white",
    borderRadius: 2,
    fontSize: "0.85rem",
    "& fieldset": { border: "1px solid #e2e8f0" },
    "&:hover fieldset": { borderColor: colorPrimary + "80" },
    "&.Mui-focused fieldset": { borderColor: colorPrimary },
  },
} as const;

const labelSx = {
  component: "label" as const,
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: 1,
  color: "#64748b",
  mb: 0.5,
  display: "block",
};

export function RelatorioFilterBar({
  values,
  onChange,
  onSearch,
  onClear,
  loading,
  title = "Filtros de Relatório",
}: RelatorioFilterBarProps) {
  const [parametros, setParametros] = useState<GrupoConcessionaria | null>(null);
  const fetchedRef = useRef(false);
  const { setToast } = useToastStore();

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const result = await fetchGrupoConcessionaria();
        if (!result?.success) {
          setToast({ variant: "error", message: result.message });
          return;
        }
        setParametros(result.data);
      } catch {
        setToast({ variant: "error", message: "Erro. Contate o administrador." });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grupoOptions: AutocompleteOption[] = [
    TODOS_GRUPOS,
    ...toOptions(parametros?.grupos ?? []),
  ];

  const concessionariaOptions: AutocompleteOption[] = [
    TODOS_CONCESSIONARIAS,
    ...toOptions(parametros?.concessionarias ?? []),
  ];

  const selectedGrupo = grupoOptions.find((o) => o.id === values.grupo) ?? TODOS_GRUPOS;
  const selectedConcessionaria =  concessionariaOptions.find((o) => o.id === values.concessionaria) ?? TODOS_CONCESSIONARIAS;

  return (
    <Paper
      elevation={0}
      sx={{ p: 3.5, borderRadius: 3, border: "1px solid #f1f5f9" }}
    >
      <Typography
        fontSize="0.7rem"
        fontWeight={700}
        textTransform="uppercase"
        letterSpacing={1.5}
        color="#94a3b8"
        mb={2.5}
      >
        {title}
      </Typography>

      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr",
          sm: "1fr 1fr",
          lg: "1fr 1fr 1fr 0.8fr",
        }}
        gap={2}
        alignItems="end"
      >
        <Box>
          <Typography {...labelSx}>Grupo</Typography>
          <Autocomplete
            size="small"
            options={grupoOptions}
            value={selectedGrupo}
            onChange={(_, opt) =>
              onChange({ ...values, grupo: opt?.id ?? "todos" })
            }
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disableClearable
            noOptionsText="Nenhum grupo encontrado"
            sx={autocompleteSx}
            renderInput={(params) => (
              <TextField {...params} placeholder="Buscar grupo..." />
            )}
          />
        </Box>

        <Box>
          <Typography {...labelSx}>Concessionária</Typography>
          <Autocomplete
            size="small"
            options={concessionariaOptions}
            value={selectedConcessionaria}
            onChange={(_, opt) =>
              onChange({ ...values, concessionaria: opt?.id ?? "todos" })
            }
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disableClearable
            noOptionsText="Nenhuma concessionária encontrada"
            sx={autocompleteSx}
            renderInput={(params) => (
              <TextField {...params} placeholder="Buscar concessionária..." />
            )}
          />
        </Box>

        <Box>
          <Typography {...labelSx}>Período</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={values.dataInicio}
              onChange={(e) =>
                onChange({ ...values, dataInicio: e.target.value })
              }
              sx={inputSx}
            />
            <Typography fontSize="0.75rem" color="#94a3b8" flexShrink={0}>
              até
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={values.dataFim}
              onChange={(e) =>
                onChange({ ...values, dataFim: e.target.value })
              }
              sx={inputSx}
            />
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Search sx={{ fontSize: "17px !important" }} />}
            disableElevation
            disabled={loading}
            onClick={onSearch}
            sx={{
              color: colorPrimary,
              borderColor: "#e2e8f0",
              fontWeight: 600,
              fontSize: "0.85rem",
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              height: 40,
              whiteSpace: "nowrap",
              bgcolor: "white",
              "&:hover": {
                borderColor: colorPrimary,
                bgcolor: colorPrimary + "08",
              },
              transition: "all 0.2s",
            }}
          >
            Gerar
          </Button>

          {onClear && (
            <Button
              variant="text"
              startIcon={<FilterAltOff sx={{ fontSize: "17px !important" }} />}
              disabled={loading}
              onClick={onClear}
              sx={{
                color: "#94a3b8",
                fontWeight: 600,
                fontSize: "0.85rem",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
                height: 40,
                whiteSpace: "nowrap",
                "&:hover": {
                  color: colorPrimary,
                  bgcolor: colorPrimary + "08",
                },
                transition: "all 0.2s",
              }}
            >
              Limpar
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
